import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { List, Spin, Button, Row, Col } from 'antd';
import pluralize from 'pluralize';

import BaseAlert from 'components/wrappers/BaseAlert';
import BasePageCard from 'components/wrappers/BasePageCard';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { AssignmentInfoTypes } from 'features/assignment/enums';
import { DashboardAssignmentResponseModel } from 'api/adminApiClient';
import { getPendingAssignmentRequests } from 'redux/reducers/dashboardSlice';
import { AdminRequestsSections } from 'features/admin/request/RequestsPage/enums';
import { useAppDispatch } from 'hooks/useAppDispatch';

const noPendningAssignmentRequestsMessage = 'No pending assignment requests.';

export default function PendingAssignmentRequests() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [pendingAsignmentRequests, setPendingAssignmentRequests] =
    useState<DashboardAssignmentResponseModel>();

  const loadPendingAssignmentRequests = useCallback(async () => {
    setIsLoading(true);
    const result = await dispatch(getPendingAssignmentRequests());
    setIsLoading(false);

    if (getPendingAssignmentRequests.fulfilled.match(result)) {
      setPendingAssignmentRequests(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  }, [dispatch]);

  useEffect(() => {
    loadPendingAssignmentRequests();
  }, [loadPendingAssignmentRequests]);

  const handleMoreClick = () => {
    navigate(`/todo?section=${AdminRequestsSections.Assignments}`);
  };

  if (isLoading) return <Spin />;

  if (!pendingAsignmentRequests) return null;

  const pluralizedAssignmentRequests = pluralize(
    'requests',
    pendingAsignmentRequests.totalCount,
    true,
  );

  return (
    <BasePageCard
      className="full-header stretch-card wrap-header"
      title="Pending Assignment Requests"
      subTitle={pluralizedAssignmentRequests}
      icons={<span className="icon icon-pending-request" />}
    >
      {!!pendingAsignmentRequests.details?.length ? (
        <>
          <List
            dataSource={pendingAsignmentRequests.details}
            renderItem={({ assignmentKey }) => (
              <List.Item>
                <Row gutter={[2, 2]}>
                  <Col xs={24} lg={12}>
                    <Link
                      className="card-link"
                      to={`/assignment/${assignmentKey}?section=${AssignmentInfoTypes.Primary}`}
                    >
                      {assignmentKey}
                    </Link>
                  </Col>
                </Row>
              </List.Item>
            )}
          />

          <div className="base-card-footer">
            <Button type="primary" onClick={handleMoreClick}>
              More
            </Button>
          </div>
        </>
      ) : (
        <span className="no-data-msg">
          {noPendningAssignmentRequestsMessage}
        </span>
      )}
    </BasePageCard>
  );
}
